import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLButtonElement} [loadMoreButton] - The load more button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /**
   * Guards against overlapping appends. `#getPage` derives the next page from the last rendered
   * card, so it only advances once an append has actually happened — two renders started before
   * the first finishes therefore resolve to the same page and append it twice. The Load More
   * button disables itself while busy, but the infinite-scroll observer has no such interlock and
   * can fire again while a page is still in flight.
   * @type {boolean}
   */
  #renderingNext = false;

  /** @type {boolean} */
  #renderingPrevious = false;

  /**
   * Invalidates page fetches that are still in flight when the filters or sort change.
   * `#handleFilterUpdate` clears the cache, but a fetch already awaiting its response resolves
   * afterwards and writes pre-filter HTML back into the cleared cache. The recovery path only
   * re-fetches the page adjacent to the last rendered card, so a poisoned entry for any later
   * page survives and is appended verbatim once the shopper scrolls that far.
   * @type {number}
   */
  #requestToken = 0;

  /**
   * Filters out cards already rendered in the grid. Shopify can return the same product on two
   * pages when the sort order is non-deterministic or inventory shifts between requests, and the
   * guards above cannot help there because the duplicate arrives in a legitimately different page.
   * @param {HTMLElement} grid - The live grid element.
   * @param {Iterable<Element>} elements - Candidate cards to add.
   * @returns {Element[]} The cards not already on screen.
   */
  #withoutRenderedCards(grid, elements) {
    const rendered = new Set(
      Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'))
        .map((card) => /** @type {HTMLElement} */ (card).dataset.productId)
        .filter(Boolean)
    );

    return Array.from(elements).filter((element) => {
      const { productId } = /** @type {HTMLElement} */ (element).dataset;
      if (!productId) return true;
      if (rendered.has(productId)) return false;
      rendered.add(productId);
      return true;
    });
  }

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext, loadMoreButton } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    // Skip auto-observing viewMoreNext when a loadMoreButton exists —
    // user must click the button to load more products.
    if (viewMoreNext && !loadMoreButton) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;

    const token = this.#requestToken;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);

    // The filters moved while this was in flight, so the response describes the old result set.
    if (token !== this.#requestToken) return;

    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    if (this.#renderingNext) return;
    this.#renderingNext = true;

    /** @type {{ page: number, url: URL } | null} */
    let appended = null;

    try {
      const { grid } = this.refs;

      if (!grid) return;

      const nextPage = this.#getPage('next');

      if (!nextPage || !this.#shouldUsePage(nextPage)) return;
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      const freshElements = this.#withoutRenderedCards(grid, nextPageItemElements);
      if (!freshElements.length) return;

      grid.append(...freshElements);
      appended = nextPage;
    } finally {
      // Released the moment the append lands. `#getPage` now reports the page after this one, so
      // a concurrent render can no longer resolve to the page just added — which is the whole job
      // of the guard. Everything below is presentation and prefetch, and holding the guard across
      // it would strand Load More entirely if one of those awaits never settled:
      // `yieldToMainThread` does not resolve while the tab is hidden.
      this.#renderingNext = false;
    }

    if (!appended) return;

    this.#aspectRatioHelper.processNewElements();

    await yieldToMainThread();

    history.pushState('', '', appended.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    /** @type {{ page: number, url: URL } | null | undefined} */
    let prepended;

    if (this.#renderingPrevious) return;
    this.#renderingPrevious = true;

    try {
      prepended = await this.#prependPreviousPage();
    } finally {
      // See #renderNextPage: released as soon as the prepend and its scroll correction are done.
      this.#renderingPrevious = false;
    }

    if (!prepended) return;

    await yieldToMainThread();

    history.pushState('', '', prepended.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * Prepends the previous page and corrects the scroll offset so the viewport does not jump.
   * @returns {Promise<{ page: number, url: URL } | null>} The page prepended, or null if none was.
   */
  async #prependPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return null;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return null;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return null;
    }

    const freshElements = this.#withoutRenderedCards(grid, previousPageItemElements);
    if (!freshElements.length) return null;

    // Store the current scroll position and height of the first element
    const currentScrollTop = getScrollTop();
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...freshElements);

    this.#aspectRatioHelper.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    return previousPage;
  }

  /**
   * Public method triggered by the Load More button click.
   * Appends the next page of products to the grid.
   */
  async loadMore() {
    const { loadMoreButton } = this.refs;
    if (!loadMoreButton) return;

    // Show busy state
    loadMoreButton.setAttribute('aria-busy', 'true');
    loadMoreButton.disabled = true;

    await this.#renderNextPage();

    // Update button visibility after render
    this.#updateLoadMoreButton();
  }

  /**
   * Checks if more pages remain and hides or resets the Load More button.
   */
  #updateLoadMoreButton() {
    const { loadMoreButton } = this.refs;
    if (!loadMoreButton) return;

    const nextPage = this.#getPage('next');
    const hasMorePages = nextPage && this.#shouldUsePage(nextPage);

    if (!hasMorePages) {
      // No more pages — hide the button container
      const container = loadMoreButton.closest('.load-more-container');

      if (container) {
        container.hidden = true;
      }
    } else {
      // Reset button state for the next click
      loadMoreButton.removeAttribute('aria-busy');
      loadMoreButton.disabled = false;
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.#requestToken++;
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
